import { CheckCircle2, CircleDashed, CircleHelp, CircleX } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { SelectItem } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { leadDispositionOptions } from '@/features/deal-review/review-options'
import { useDealDecisionOptions } from '@/hooks/use-deal-leads'
import { itemToneClasses } from '@/features/deal-review/review-tones'
import type {
  ReviewOption,
  ReviewOptionIcon,
} from '@/features/deal-review/review-options'

// Tints live in review-tones.ts so this module stays fast-refresh safe.

const optionIcons: Record<ReviewOptionIcon, LucideIcon> = {
  unreviewed: CircleDashed,
  approve: CheckCircle2,
  reject: CircleX,
  'needs-information': CircleHelp,
}

export function ReviewOptionContent({
  option,
  compact = false,
  showIcon = true,
}: {
  option: ReviewOption<string>
  compact?: boolean
  showIcon?: boolean
}) {
  const Icon = optionIcons[option.icon]

  return (
    <Typography
      as="span"
      variant="body-sm"
      className="flex min-w-0 items-center gap-2"
    >
      {showIcon && <Icon aria-hidden="true" className="size-3.5 shrink-0" />}
      <Typography as="span" variant="body-sm" className="truncate">
        {compact ? option.compactLabel : option.label}
      </Typography>
    </Typography>
  )
}

export function DispositionSelectItems() {
  const offered: string[] = useDealDecisionOptions().data?.dispositions ?? []
  return leadDispositionOptions
    .filter((item) => offered.includes(item.value))
    .map((item) => (
      <SelectItem
        key={item.value}
        value={item.value}
        className={itemToneClasses[item.tone]}
      >
        <ReviewOptionContent option={item} />
      </SelectItem>
    ))
}

export function RecommendationSummary({
  summary,
  reason,
  footnote,
}: {
  summary: string
  reason?: string
  footnote?: string
}) {
  return (
    <>
      <Typography as="span" variant="body-sm" className="font-medium">
        {summary}
      </Typography>
      {reason && (
        <Typography variant="body-sm" className="mt-1 whitespace-normal">
          {reason}
        </Typography>
      )}
      {footnote && (
        <Typography
          variant="body-sm"
          className="mt-1 text-primary-foreground/70"
        >
          {footnote}
        </Typography>
      )}
    </>
  )
}
